import React, { useContext, useEffect, useState } from 'react';
import common from '../../common.module.scss';
import {
    getSystems,
    updateSystem,
    createSystem,
    deleteSystem
} from '../../api/adminApi';
import ConfirmDeleteModal from '../ConfirmDeleteModal.jsx';
import CreateEntityModal from "../CreateEntityModal.jsx";
import { toast } from "react-toastify";
import Pagination from '../pagination';
import { EmployeeContext } from "../../context/EmployeeContext.jsx";
import useIsMobile from "@/shared/lib/hooks/useIsMobile";
import { FaCheck } from "react-icons/fa6";
import { ImCross } from "react-icons/im";
import { RiDeleteBinLine } from "react-icons/ri";
import { GoPencil } from "react-icons/go";
import { GiSettingsKnobs } from "react-icons/gi";
import FilterModal from "@/shared/ui/FiltersModal";

export default function SystemTable() {
    const { employee } = useContext(EmployeeContext);
    const role = employee?.role;

    const PAGE_SIZE = 10;

    const [systems, setSystems] = useState([]);
    const [, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [editId, setEditId] = useState(null);
    const [editedName, setEditedName] = useState('');
    const [editedMinAge, setEditedMinAge] = useState(0);
    const [editedCyberpunk, setEditedCyberpunk] = useState(false);
    const [saving, setSaving] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [systemToDelete, setSystemToDelete] = useState(null);

    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [direction, setDirection] = useState('asc');

    const [showModalFilters, setShowModalFilters] = useState(false);

    const isMobile = useIsMobile();

    useEffect(() => {
        loadSystems();
    }, [page, search, sortBy, direction]);

    const loadSystems = async () => {
        try {
            setLoading(true);
            const params = {
                page,
                size: PAGE_SIZE,
                ...(search && { search }),
                ...(sortBy && { sortBy }),
                ...(sortBy && { direction }),
            };
            const res = await getSystems(params);
            const list = res.data.content || res.data;
            setSystems(list.map(s => ({
                id: s.id,
                name: s.name,
                enabled: s.isActive,
                minAge: s.minAge || 14,
                isCyberpunkSystem: !!s.isCyberpunkSystem,
            })));
            setTotalPages(res.data.totalPages || 1);
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при загрузке систем';
            toast.error(errorMessage);
            setError('Ошибка при загрузке систем');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy !== field) {
            setSortBy(field);
            setDirection('desc');
        } else if (direction === 'desc') {
            setDirection('asc');
        } else {
            setSortBy('');
            setDirection('asc');
        }
        setPage(0);
    };

    const renderSortArrow = (field) => {
        if (sortBy !== field) return '';
        return direction === 'asc' ? ' ↑' : ' ↓';
    };

    const toggleSwitch = async (id) => {
        const system = systems.find(s => s.id === id);
        const newState = !system.enabled;

        try {
            await updateSystem(id, {
                name: system.name,
                isActive: newState,
                minAge: system.minAge,
                isCyberpunkSystem: system.isCyberpunkSystem,
            });

            setSystems(prev =>
                prev.map(s => s.id === id ? { ...s, enabled: newState } : s)
            );
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при обновлении системы';
            toast.error(errorMessage);
        }
    };

    const handleEdit = (system) => {
        setEditId(system.id);
        setEditedName(system.name);
        setEditedMinAge(system.minAge);
        setEditedCyberpunk(system.isCyberpunkSystem);
    };

    const handleCancel = () => {
        setEditId(null);
        setEditedName('');
        setEditedMinAge(0);
        setEditedCyberpunk(false);
    };

    const handleSave = async (system) => {
        if (!editedName.trim()) {
            toast.error('Поле не может быть пустым');
            return;
        }

        const parsedAge = parseInt(editedMinAge, 10);
        if (isNaN(parsedAge) || parsedAge < 0) {
            toast.error('Минимальный возраст должен быть положительным числом');
            return;
        }

        setSaving(true);
        try {
            await updateSystem(system.id, {
                name: editedName,
                minAge: parsedAge,
                isActive: system.enabled,
                isCyberpunkSystem: editedCyberpunk,
            });

            setSystems(prev =>
                prev.map(s =>
                    s.id === system.id
                        ? {
                            ...s,
                            name: editedName,
                            minAge: parsedAge,
                            isCyberpunkSystem: editedCyberpunk
                        }
                        : s
                )
            );

            handleCancel();
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при сохранении';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // ВАЖНО:
    // текущий CreateEntityModal у вас умеет только текст + второй инпут.
    // Для создания с флагом ниже нужен либо отдельный чекбокс в модалке,
    // либо доработка CreateEntityModal.
    const handleCreateSystem = async (name, ageStr) => {
        const parsedAge = parseInt(ageStr, 10);

        if (isNaN(parsedAge) || parsedAge < 0) {
            toast.error('Минимальный возраст должен быть положительным числом');
            return;
        }

        try {
            const res = await createSystem({
                name,
                minAge: parsedAge,
                isActive: true,
                isCyberpunkSystem: false,
            });

            setSystems(prev => [
                ...prev,
                {
                    id: res.data.id,
                    name: res.data.name,
                    enabled: res.data.isActive,
                    minAge: res.data.minAge,
                    isCyberpunkSystem: !!res.data.isCyberpunkSystem,
                }
            ]);
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при создании системы';
            toast.error(errorMessage);
        }
    };

    const confirmDelete = async () => {
        if (!systemToDelete) return;
        try {
            await deleteSystem(systemToDelete.id);
            setSystems(prev => prev.filter(s => s.id !== systemToDelete.id));
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при удалении';
            toast.error(errorMessage);
        } finally {
            setSystemToDelete(null);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) setPage(newPage);
    };

    const handleChangeSort = (field, dir) => {
        setSortBy(field);
        setDirection(dir);
        setPage(0);
    };

    const sortOptions = [
        { value: 'name', label: 'Название' },
        { value: 'minAge', label: 'Возраст' },
        { value: 'isActive', label: 'Состояние' },
        { value: 'isCyberpunkSystem', label: 'Cyberpunk' },
    ];

    if (error) return <h2>{error}</h2>;

    return (
        <div className={`container-fluid ${common.container}`}>
            {isMobile && (<h1 className={common.mobileHeading}>Системы</h1>)}

            <div className="mb-3">
                <div className="row align-items-center justify-content-between">
                    <div className="col-md-5 col-10">
                        <input
                            type="text"
                            placeholder="Поиск по названию"
                            className={`form-control w-100 ${common.searchInput}`}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                        />
                    </div>

                    {isMobile && (
                        <div className="col-2 d-flex justify-content-end">
                            <button
                                type="button"
                                className={`${common.iconButton} ms-2`}
                                onClick={() => setShowModalFilters(true)}
                                aria-label="Открыть сортировку"
                            >
                                <GiSettingsKnobs size={20} />
                            </button>
                        </div>
                    )}

                    {(role !== 'ADMIN' && role !== 'MASTER') && !isMobile && (
                        <div className={"col-md-5 justify-content-end d-flex"}>
                            <button
                                className="service-btn ms-5"
                                onClick={() => setShowModal(true)}
                            >
                                Добавить систему
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="d-flex justify-content-between mb-4">
                <div style={{ width: '100%' }}>
                    <table className={`table ${common.table}`}>
                        <thead className={common.tableHeader}>
                        <tr>
                            <th className={common.sortable} onClick={() => handleSort('name')}>
                                Название{renderSortArrow('name')}
                            </th>
                            <th className={common.sortable} onClick={() => handleSort('minAge')}>
                                Возраст{renderSortArrow('minAge')}
                            </th>
                            <th className={common.sortable} onClick={() => handleSort('isCyberpunkSystem')}>
                                Cyberpunk{renderSortArrow('isCyberpunkSystem')}
                            </th>
                            <th className={common.sortable} onClick={() => handleSort('isActive')}>
                                Состояние{renderSortArrow('isActive')}
                            </th>
                        </tr>
                        </thead>

                        <tbody>
                        {systems.map((system, index) => (
                            <tr key={system.id} className={index % 2 === 1 ? common.altRow : ''}>
                                <td className="align-middle">
                                    {editId === system.id ? (
                                        <input
                                            className="form-control"
                                            value={editedName}
                                            onChange={e => setEditedName(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') handleSave(system);
                                                if (e.key === 'Escape') handleCancel();
                                            }}
                                            autoFocus
                                        />
                                    ) : (
                                        system.name
                                    )}
                                </td>

                                <td className="align-middle">
                                    {editId === system.id ? (
                                        <input
                                            type="number"
                                            min={0}
                                            className="form-control"
                                            value={editedMinAge}
                                            onChange={e => setEditedMinAge(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') handleSave(system);
                                                if (e.key === 'Escape') handleCancel();
                                            }}
                                        />
                                    ) : (
                                        `${system.minAge}+`
                                    )}
                                </td>

                                <td className="align-middle">
                                    {editId === system.id ? (
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={editedCyberpunk}
                                                onChange={(e) => setEditedCyberpunk(e.target.checked)}
                                            />
                                        </div>
                                    ) : (
                                        system.isCyberpunkSystem ? 'Да' : 'Нет'
                                    )}
                                </td>

                                <td className="align-middle">
                                    <div className="d-flex align-items-center">
                                        <div className="form-check form-switch me-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={system.enabled}
                                                onChange={() => toggleSwitch(system.id)}
                                            />
                                        </div>

                                        {editId === system.id ? (
                                            <>
                                                <button
                                                    className={`btn-word black btn-link p-0 me-2 ${common.editBtn}`}
                                                    onClick={() => handleSave(system)}
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Сохранение...' : isMobile ? <FaCheck className={common.icon}/> : "Сохранить"}
                                                </button>
                                                <button
                                                    className={`btn-word red btn-link p-0 me-2 ${common.editBtn}`}
                                                    onClick={handleCancel}
                                                    disabled={saving}
                                                >
                                                    {isMobile ? <ImCross className={common.icon}/> : "Отменить"}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                {(role !== 'ADMIN' && role !== 'MASTER') && (
                                                    <>
                                                        <span
                                                            className={`btn-word black btn-link p-0 me-2 ${common.editBtn}`}
                                                            onClick={() => handleEdit(system)}
                                                        >
                                                            {isMobile ? <GoPencil className={common.icon}/> : "Редактировать"}
                                                        </span>
                                                        <span
                                                            className={`btn-word red btn-link p-0 ${common.editBtn}`}
                                                            onClick={() => setSystemToDelete(system)}
                                                        >
                                                            {isMobile ? <RiDeleteBinLine className={common.icon}/> : "Удалить"}
                                                        </span>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />

                    {(role !== 'ADMIN' && role !== 'MASTER') && isMobile && (
                        <div className="col-12 justify-content-end d-flex">
                            <button
                                className="service-btn w-100 mt-5"
                                onClick={() => setShowModal(true)}
                            >
                                Добавить систему
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <CreateEntityModal
                    title="Добавить систему"
                    placeholder="Название системы"
                    onClose={() => setShowModal(false)}
                    onSave={handleCreateSystem}
                    secondInput
                    secondPlaceholder="Минимальный возраст"
                    checkbox
                    checkboxLabel="Cyberpunk система"
                />
            )}

            {systemToDelete && (
                <ConfirmDeleteModal
                    roomName={systemToDelete.name}
                    onConfirm={confirmDelete}
                    onCancel={() => setSystemToDelete(null)}
                />
            )}

            <FilterModal
                isOpen={showModalFilters}
                onClose={() => setShowModalFilters(false)}
                sortOptions={sortOptions}
                sortBy={sortBy}
                direction={direction}
                onChangeSort={handleChangeSort}
            />
        </div>
    );
}